import colors from "styles/colors";

const button = {
  baseStyle: ({ invert }) => ({
    fontWeight: "bold",
    fontFamily: "body",
    borderRadius: "0px",
    textTransform: "uppercase",
    letterSpacing: "2px",
    transition: "0.3s ease",
    outlineColor: invert ? "black.900" : "white",
    outline: "0px",
    bg: "rgba(0,0,0,0)",
    _disabled: {
      opacity: 0.4,
      cursor: "not-allowed",
      boxShadow: "none",
    },
  }),
  sizes: {
    regular: {
      px: "28px",
      py: "12px",
      fontSize: ["12px", null, "14px"],
      lineHeight: [1.4, null, 1.43],
      letterSpacing: "2px",
      fontWeight: "bold",
    },
    small: {
      px: ["14px", null, "18px"],
      py: ["4px", null, "8px"],
      fontSize: "12px",
      lineHeight: 1.33,
    },
  },
  variants: {
    pill: ({ invert }) => ({
      borderRadius: "full",
      border: "1px solid",
      borderColor: invert ? "black.900" : "whiteAlpha.200",
      color: invert ? "black.900" : "white",
      bg: "transparent",
      zIndex: "1",
      transition: "all 0.2s ease",
      _hover: {
        bg: invert ? "black.100" : "whiteAlpha.100",
        borderColor: invert ? "black.900" : "whiteAlpha.300",
        textDecoration: "none",
      },
      '&[data-auto-hover="true"]': {
        bg: invert ? "black.100" : "whiteAlpha.100",
      },
      _active: {
        bg: invert ? "black.200" : "whiteAlpha.200",
        boxShadow: "none",
        outline: "none",
      },
      _focus: {
        outline: "none",
        boxShadow: invert
          ? `0 0 2px 1px ${colors.primary}`
          : "0 0 2px 2px #ffffffa0",
      },
    }),

    outline: ({ invert }) => ({
      border: "2px",
      borderColor: invert ? "black.900" : "white",
      color: invert ? "black" : "white",
      zIndex: "1",
      _before: {
        content: '""',
        position: "absolute",
        bg: invert ? "black.900" : "white",
        width: "0px",
        height: "100%",
        left: "auto",
        right: "0px",
        top: "0px",
        transition: "width 0.6s ease",
        zIndex: "-1",
      },
      _hover: {
        bg: null,
        color: invert ? "white" : "black.900",
        textDecoration: "none",
        _before: {
          left: "0px",
          right: "auto",
          width: "100%",
          transition: "width 0.4s ease",
        },
      },
      '&[data-auto-hover="true"]': {
        bg: null,
        color: invert ? "white" : "black.900",
        textDecoration: "none",
        _before: {
          left: "0px",
          right: "auto",
          width: "100%",
          transition: "width 0.4s ease",
        },
      },
      _active: {
        color: invert ? "white" : "black.900",
        bg: null,
        boxShadow: "none",
        outline: "none",
        _before: {
          left: "0px",
          right: "auto",
          width: "100%",
          transition: "width 0.4s ease",
        },
      },
      _focus: {
        bg: null,
        outline: "none",
        boxShadow: invert
          ? `0 0 2px 1px ${colors.primary}`
          : "0 0 2px 2px #ffffffa0",
      },
    }),

    selected_link: ({ invert }) => ({
      boxShadow: "0px 0px 0px",
      borderRadius: "0px",
      px: "0px",
      py: "12px",
      fontSize: "12px",
      lineHeight: 1.67,
      pl: "4px",
      letterSpacing: "4px",
      bg: `linear-gradient(to right, ${colors.primary} 50%, transparent 50%)`,
      backgroundRepeat: "no-repeat",
      backgroundSize: "200% 2px",
      backgroundPosition: "left bottom",
      color: invert ? "black.900" : "white",
      _focus: {
        boxShadow: "0px 0px 0px",
        borderRadius: "0px",
      },
      _active: {
        color: invert ? "black.900" : "white",
        borderRadius: "0px",
      },
    }),

    link: ({ invert }) => ({
      color: invert ? "black.900" : "white",
      borderRadius: "0px",
      px: "0px",
      py: "12px !important",
      fontSize: "12px",
      lineHeight: 1.67,
      pl: "4px !important",
      letterSpacing: "4px",
      backgroundSize: "200% 2px",
      zIndex: "1",
      _before: {
        content: '""',
        position: "absolute",
        bg: "primary",
        width: "0px",
        height: "2px",
        left: "auto",
        right: "0px",
        bottom: "0px",
        transition: "width 0.6s ease",
        zIndex: "-1",
      },
      _hover: {
        bg: null,
        textDecoration: "none",
        _before: {
          left: "0px",
          right: "auto",
          width: "100%",
          transition: "width 0.4s ease",
        },
      },
      _focus: {
        boxShadow: "0px 0px 0px",
        py: "0px",
      },
      _active: {
        color: invert ? "black.900" : "white",
        borderRadius: "0px",
        py: "12px",
        _before: {
          left: "0px",
          right: "auto",
          width: "100%",
          transition: "width 0.4s ease",
        },
      },
    }),

    text: () => ({
      color: "primary",
      borderRadius: "0px",
      px: "0px",
      py: "0px !important",
      textTransform: "none",
      letterSpacing: "1px",
      fontSize: "12px",
      pl: "0px !important",
    }),

    tab: ({ selected }) => ({
      color: selected ? "primary" : "white",
      borderRadius: "0px",
      px: "0px",
      py: "8px !important",
      fontSize: "12px",
      lineHeight: 1.67,
      pr: "2px !important",
      bg: `linear-gradient(to right, ${colors.primary} 50%, transparent 50%)`,
      backgroundRepeat: "no-repeat",
      backgroundSize: "202% 2px",
      boxShadow: "none",
      backgroundPosition: selected ? "left bottom" : "right bottom",
      textDecoration: "none",
      _hover: {
        opacity: 0.8,
      },
    }),

    tag: ({ selected }) => ({
      color: selected ? "white" : "gray.400",
      p: "6px !important",
      fontSize: "12px",
      lineHeight: 1,
      border: `1px solid ${colors.gray[400]}`,
      bg: `linear-gradient(to right, ${colors.gray[400]} 50%, transparent 50%)`,
      backgroundRepeat: "no-repeat",
      backgroundSize: "200% 100%",
      boxShadow: "none",
      backgroundPosition: selected ? "left bottom" : "right bottom",
      textDecoration: "none",
      _hover: {
        opacity: 0.7,
      },
    }),

    floating: ({ invert }) => ({
      color: invert ? "black.900" : "white",
      borderRadius: "0px",
      px: "lg",
      fontSize: "12px",
      fontWeight: "bold",
      lineHeight: "16px",
      letterSpacing: "2px",
      boxShadow: "none",
      textDecoration: "none",
    }),

    menu: ({ invert, small, selected }) => ({
      color: invert ? "black.900" : "white",
      opacity: selected ? 1 : 0.7,
      borderRadius: "0px",
      p: "0px",
      fontSize: small
        ? ["14px", "14px", "12px", "12px", "14px"]
        : ["20px", "20px", "14px", "14px", "18px", "20px"],
      lineHeight: small
        ? ["14px", "14px", "12px", "12px", "14px"]
        : ["20px", "20px", "14px", "14px", "18px", "20px"],
      mb: small ? "lg" : 0,
      mr: small ? "lg" : "md",
      letterSpacing: small ? "1px" : ["2px", "2px", "2px", "1px", "2px", "2px"],
      _focus: {
        boxShadow: "0px 0px 0px",
        opacity: 1,
        p: "0px",
      },
      _active: {
        color: invert ? "black.900" : "white",
        borderRadius: "0px",
        opacity: 1,
        p: "0px",
      },
      _hover: {
        boxShadow: "0px 0px 0px",
        opacity: 1,
        borderRadius: "0px",
        textDecoration: "none",
        p: "0px",
      },
    }),

    solid: ({ invert }) => ({
      border: `2px solid ${invert ? colors.black[900] : "white"}`,
      color: invert ? "white" : "black.900",
      bg: invert ? "black.900" : "white",
      zIndex: "1",
      _before: {
        content: '""',
        position: "absolute",
        bg: invert ? colors.gray[800] : colors.gray[300],
        width: "0px",
        height: "100%",
        left: "auto",
        right: "0px",
        top: "0px",
        transition: "width 0.6s ease",
        zIndex: "-1",
      },
      _hover: {
        bg: null,
        _before: {
          left: "0px",
          right: "auto",
          width: "100%",
          transition: "width 0.4s ease",
        },
      },
      '&[data-auto-hover="true"]': {
        bg: null,
        _before: {
          left: "0px",
          right: "auto",
          width: "100%",
          transition: "width 0.4s ease",
        },
      },
      _active: {
        bg: null,
        outline: "none",
        boxShadow: "none",
        _before: {
          left: "0px",
          right: "auto",
          width: "100%",
          transition: "width 0.4s ease",
        },
      },
      _focus: {
        bg: null,
        outline: "none",
        boxShadow: "0 0 2px 2px #fffdfd80",
      },
    }),

    link_bt: () => ({
      color: "white",
      fontSize: ["10px", null, "12px"],
      lineHeight: 1,
      p: 0,
      zIndex: "1",
      _hover: {
        textDecoration: "none",
        color: "whiteAlpha.800",
      },
      _active: {
        color: "whiteAlpha.800",
      },
    }),

    solid_secondary_light: () => ({
      color: "white",
      backgroundColor: "primaryDark",
      borderLeft: `6px solid ${colors.primaryLight}`,
      zIndex: "1",
      overflow: "hidden",
      position: "relative",
      _after: {
        background: "#fff",
        content: '""',
        height: 155,
        left: -400,
        opacity: 0.2,
        position: "absolute",
        top: -50,
        transform: "rotate(35deg)",
        transition: "all 1250ms cubic-bezier(0.19, 1, 0.22, 1)",
        width: 50,
        zIndex: -10,
      },
      _hover: {
        _after: {
          left: 400,
          transition: "all 1250ms cubic-bezier(0.19, 1, 0.22, 1)",
        },
      },
      '&[data-auto-hover="true"]': {
        _after: {
          left: 400,
          transition: "all 1250ms cubic-bezier(0.19, 1, 0.22, 1)",
        },
      },
    }),

    solid_primary: () => ({
      border: `2px solid ${colors.primary}`,
      color: "white",
      bg: colors.primary,
      zIndex: "1",
      _before: {
        content: '""',
        position: "absolute",
        bg: "rgba(255, 255, 255, 0.1)",
        width: "0px",
        height: "100%",
        left: "auto",
        right: "0px",
        top: "0px",
        transition: "width 0.6s ease",
        zIndex: "-1",
      },
      _hover: {
        bg: null,
        _before: {
          left: "0px",
          right: "auto",
          width: "100%",
          transition: "width 0.4s ease",
        },
      },
      '&[data-auto-hover="true"]': {
        bg: null,
        _before: {
          left: "0px",
          right: "auto",
          width: "100%",
          transition: "width 0.4s ease",
        },
      },
      _active: {
        bg: null,
        outline: "none",
        boxShadow: "none",
        _before: {
          left: "0px",
          right: "auto",
          width: "100%",
          transition: "width 0.4s ease",
        },
      },
      _focus: {
        bg: null,
        outline: "none",
        boxShadow: "0 0 2px 2px #fffdfd80",
      },
    }),
  },
  defaultProps: {
    size: "regular",
    variant: "outline",
  },
};

export default button;
